<template>
  <div class="container-fluid py-4">
    <!-- Main Card -->
    <div class="card main-shop-card border-0 shadow-sm">
      <div class="card-body p-4">
        <!-- Header -->
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
          <div>
            <h4 class="page-title mb-1">กลุ่มสาขา</h4>
            <p class="page-subtitle mb-0">
              รวมหลายสาขาเพื่อดูภาพรวมและตั๋วครบกำหนดรวมกัน — เลือกได้ที่
              <router-link to="/generals/dashboard" class="dashboard-link">Dashboard</router-link>
            </p>
          </div>
          <div>
            <button class="btn btn-add-group d-inline-flex align-items-center gap-2" @click="openAddModal">
              <span class="btn-icon-plus">+</span>
              <span>เพิ่มกลุ่มสาขา</span>
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="table-responsive rounded-3 border">
          <table class="table align-middle mb-0 custom-table">
            <thead class="table-dark-green">
              <tr>
                <th class="text-center col-index">#</th>
                <th class="col-name">ชื่อกลุ่ม</th>
                <th class="col-members">สาขาในกลุ่ม</th>
                <th class="text-center col-action">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              <!-- Loading State -->
              <tr v-if="isLoading">
                <td colspan="4" class="text-center py-5">
                  <div class="spinner-border spinner-border-sm text-success me-2" role="status"></div>
                  <span class="text-muted">กำลังโหลดข้อมูล...</span>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-else-if="shopGroups.length === 0">
                <td colspan="4" class="text-center py-5 text-muted">
                  <div class="empty-state">
                    <span class="material-icons display-6 text-secondary mb-2">folder_off</span>
                    <div>ยังไม่มีข้อมูลกลุ่มสาขา</div>
                    <small class="text-muted">คลิกปุ่ม "เพิ่มกลุ่มสาขา" เพื่อเริ่มต้นสร้างกลุ่ม</small>
                  </div>
                </td>
              </tr>

              <!-- Data Rows -->
              <tr v-for="(group, index) in shopGroups" v-else :key="group.uid || index" class="table-row">
                <!-- Column 1: Index -->
                <td class="text-center text-muted fw-semibold">
                  {{ index + 1 }}
                </td>

                <!-- Column 2: Group Name & Branch Count -->
                <td>
                  <div class="group-name">{{ group.name }}</div>
                  <div class="branch-count-text">
                    {{ group.members ? group.members.length : 0 }} สาขา
                  </div>
                </td>

                <!-- Column 3: Branch Badges -->
                <td>
                  <div class="d-flex flex-wrap gap-2 py-1">
                    <span
                      v-for="(member, mIdx) in (group.members || [])"
                      :key="mIdx"
                      class="branch-pill"
                    >
                      {{ getShopName(member) }}
                    </span>
                    <span v-if="!group.members || group.members.length === 0" class="text-muted small">
                      (ไม่มีสาขา)
                    </span>
                  </div>
                </td>

                <!-- Column 4: Actions (Edit / Delete) -->
                <td class="text-center">
                  <div class="d-inline-flex align-items-center gap-2">
                    <!-- Edit Button -->
                    <button
                      type="button"
                      class="btn-action-circle btn-action-edit"
                      title="แก้ไข"
                      @click="openEditModal(group)"
                    >
                      <svg class="action-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                      </svg>
                    </button>

                    <!-- Delete Button -->
                    <button
                      type="button"
                      class="btn-action-circle btn-action-delete"
                      title="ลบ"
                      @click="handleDelete(group)"
                    >
                      <svg class="action-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal เพิ่ม/แก้ไขกลุ่มสาขา -->
    <div
      id="shopGroupModal"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="shopGroupModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content custom-modal-content border-0">
          <!-- Modal Header -->
          <div class="modal-header border-bottom-0 pb-0 pt-4 px-4 align-items-center">
            <h5 id="shopGroupModalLabel" class="modal-title fw-bold text-dark-title">
              {{ isEditMode ? 'แก้ไขกลุ่มสาขา' : 'เพิ่มกลุ่มสาขา' }}
            </h5>
            <button
              type="button"
              class="btn-close-modal"
              aria-label="Close"
              @click="closeModal"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body px-4 py-3">
            <!-- 1. ชื่อกลุ่ม -->
            <div class="mb-3">
              <label class="form-label text-form-label mb-2">ชื่อกลุ่ม</label>
              <input
                v-model="form.name"
                type="text"
                class="form-control custom-input"
                placeholder=""
                :class="{ 'is-invalid': showNameError && !form.name.trim() }"
              />
            </div>

            <!-- 2. ค้นหาสาขา (รหัส/ชื่อ) & เลือกทั้งหมด -->
            <div class="mb-3">
              <label class="form-label text-form-label mb-2">ค้นหาสาขา (รหัส/ชื่อ)</label>
              <div class="d-flex align-items-center gap-2">
                <input
                  v-model="searchBranchText"
                  type="text"
                  class="form-control custom-input flex-grow-1"
                  placeholder=""
                />
                <button
                  type="button"
                  class="btn btn-outline-select-all text-nowrap"
                  @click="toggleSelectAll"
                >
                  {{ isAllFilteredSelected ? 'ยกเลิกการเลือกทั้งหมด' : 'เลือกทั้งหมด' }}
                </button>
              </div>
            </div>

            <!-- 3. ข้อความนับจำนวนสาขาที่เลือก -->
            <div class="branch-counter-text mb-2">
              เลือกแล้ว {{ form.members.length }} จาก {{ allowedBranches.length }} สาขา
            </div>

            <!-- 4. ตารางรายการสาขา -->
            <div class="branch-table-container rounded-3 border">
              <table class="table mb-0 branch-table align-middle">
                <thead class="table-dark-green">
                  <tr>
                    <th style="width: 50px;" class="text-center"></th>
                    <th style="width: 130px;">สาขา</th>
                    <th>ชื่อสาขา</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="filteredModalBranches.length === 0">
                    <td colspan="3" class="text-center py-4 text-muted">
                      ไม่พบสาขาที่ค้นหา
                    </td>
                  </tr>
                  <tr
                    v-for="branch in filteredModalBranches"
                    v-else
                    :key="branch.uid || branch.code"
                    class="branch-row cursor-pointer"
                    @click="toggleBranch(branch)"
                  >
                    <td class="text-center" @click.stop>
                      <material-checkbox :id="'chk-' + (branch.uid || branch.code)" :name="'branch-' + (branch.uid || branch.code)" :checked="isBranchSelected(branch)" @change="toggleBranch(branch)"/>
                    </td>
                    <td class="branch-code-cell">{{ branch.code }}</td>
                    <td class="branch-name-cell">{{ branch.nameTh || branch.nameEn }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Validation Error / Hint -->
            <div class="validation-hint mt-2" :class="{ 'text-danger': showNameError && !form.name.trim() }">
              กรุณาตั้งชื่อกลุ่ม
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer border-top-0 pt-0 pb-4 px-4 d-flex justify-content-end gap-2">
            <!-- ยกเลิก (Red) -->
            <button
              type="button"
              class="btn btn-modal-cancel d-inline-flex align-items-center gap-1"
              @click="closeModal"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span>ยกเลิก</span>
            </button>

            <!-- บันทึก (Green) -->
            <button
              type="button"
              class="btn btn-modal-save d-inline-flex align-items-center gap-1"
              :disabled="isSubmitting"
              @click="handleSave"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              <span>{{ isSubmitting ? 'กำลังบันทึก...' : 'บันทึก' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { reactive, inject, computed, ref, onMounted } from 'vue'
import { useI18n } from "vue-i18n"
import Modal from 'bootstrap/js/dist/modal'
import MaterialCheckbox from "@/components/MaterialCheckbox.vue";
import MaterialInput from "@/components/MaterialInput.vue";
import MaterialButton from "@/components/MaterialButton.vue";

import { useShopGroupStore } from '@/stores/shopgroup'
import { useUserSecurityStore } from "@/stores/usersecurity"
import { useShopStore } from "@/stores/shop"
import { clearShopGroupCache } from '@/utils/shopGroup'

const { t } = useI18n()
const swal = inject('$swal')

const sgroupStore = useShopGroupStore()
const userSStore = useUserSecurityStore()
const shopStore = useShopStore()

// State
const isLoading = ref(false)
const isSubmitting = ref(false)
const currentUserUid = ref('')
const allowedShopUids = ref([])
const allShops = ref([])
const shopGroups = ref([])

// Modal Form State
const isEditMode = ref(false)
const searchBranchText = ref('')
const showNameError = ref(false)
const originalMembers = ref([])

const form = reactive({
  uid: '',
  name: '',
  members: [],
  version: 0
})

// Branches allowed for this user (filtered by shop_uids from introspect)
const allowedBranches = computed(() => {
  if (allowedShopUids.value.length === 0) {
    return allShops.value
  }
  return allShops.value.filter(s => {
    return (
      allowedShopUids.value.includes(s.uid) ||
      allowedShopUids.value.includes(String(s.uid)) ||
      allowedShopUids.value.includes(s.code)
    )
  })
})

// Branches filtered by search keyword in Modal
const filteredModalBranches = computed(() => {
  const query = searchBranchText.value.trim().toLowerCase()
  if (!query) return allowedBranches.value
  return allowedBranches.value.filter(s => {
    const code = (s.code || '').toLowerCase()
    const nameTh = (s.nameTh || '').toLowerCase()
    const nameEn = (s.nameEn || '').toLowerCase()
    return code.includes(query) || nameTh.includes(query) || nameEn.includes(query)
  })
})

// Check if all currently filtered branches are selected
const isAllFilteredSelected = computed(() => {
  const list = filteredModalBranches.value
  if (list.length === 0) return false
  return list.every(b => isBranchSelected(b))
})

// Initialize Data
onMounted(async () => {
  await initData()
})

async function initData() {
  isLoading.value = true
  try {
    // 1. Introspect: get user_uid and shop_uids
    await introspect()

    // 2. Load shop list
    await loadShops()

    // 3. Fetch shop groups
    await shopGroupListFetch()
  } catch (error) {
    console.error("Init data error:", error)
  } finally {
    isLoading.value = false
  }
}

// 1. Introspect API
async function introspect() {
  try {
    const key = JSON.parse(localStorage.getItem("FR-SDK-nvtx-admin"))
    const token = key?.accessToken || ""
    const res = await userSStore.introspect(token)
    // console.log("introspect res:", res)

    if (res) {
      // Extract user_uid
      currentUserUid.value = res.user_uid || res.data?.user_uid || ""

      // Extract shop_uids (comma-separated string e.g. "5646...,5646...")
      const rawShopUids = res.shop_uids || res.data?.shop_uids || ""
      if (typeof rawShopUids === "string" && rawShopUids.trim() !== "") {
        allowedShopUids.value = rawShopUids.split(",").map(id => id.trim()).filter(Boolean)
      } else if (Array.isArray(rawShopUids)) {
        allowedShopUids.value = rawShopUids
      }
      // console.log("Allowed shop UIDs:", allowedShopUids.value)
    }
  } catch (error) {
    console.error("introspect error:", error)
  }
}

// 2. Load Shops (cached from localStorage, fallback to API)
async function loadShops() {
  const cached = localStorage.getItem("shoplist")
  if (cached) {
    try {
      allShops.value = JSON.parse(cached)
    } catch (e) {
      console.error("Error parsing shoplist from localStorage", e)
    }
  }

  if (!allShops.value || allShops.value.length === 0) {
    try {
      const res = await shopStore.shopListFetch({ index: 0, size: 100 })
      if (res?.data?.data?.list) {
        allShops.value = res.data.data.list
      }
    } catch (e) {
      console.error("Error fetching shop list:", e)
    }
  }
}

// 3. Fetch Shop Groups
async function shopGroupListFetch() {
  try {
    const payload = {
      index: 0,
      size: 100,
      userUid: currentUserUid.value
    }
    const res = await sgroupStore.shopGroupListFetch(payload)
    // console.log("shopGroupListFetch res:", res)

    if (res && res.data) {
      let rawList = []
      
      // เช็คโครงสร้างข้อมูลเหมือนที่คุณเขียนไว้เดิม
      if (res.data.rtnDesc === "success" && res.data.data?.list) {
        rawList = res.data.data.list
      } else if (Array.isArray(res.data.data?.list)) {
        rawList = res.data.data.list
      } else if (Array.isArray(res.data.data)) {
        rawList = res.data.data
      } else if (Array.isArray(res.data)) {
        rawList = res.data
      }

      // 🟢 ดึงข้อมูลสมาชิก (members) มาเติมเต็มให้กับทุก item ในรายการ
      const completedList = await Promise.all(
        rawList.map(async (group) => {
          // ดึงข้อมูลรายกลุ่มด้วย uid
          const infoRes = await sgroupStore.shopGroupInfoFetch({ uid: group.uid })
          
          // ตรวจสอบโครงสร้าง response ของ Info (ตัวอย่างนี้เช็ค res.data.data.members)
          // ปรับแก้เส้นทางของอาร์เรย์ members ให้ตรงกับที่ API ส่งกลับมาจริงนะครับ
          const membersData = infoRes?.data?.data?.members || []
          
          return {
            ...group,
            members: membersData
          }
        })
      )

      // นำผลลัพธ์ที่เติม members ครบแล้วไปเซ็ตให้เซสชัน/State
      shopGroups.value = completedList
    }
  } catch (error) {
    console.error("shopGroupListFetch error:", error)
  }
}

// Helper: Get shop name from member (object or legacy code/uid)
function getShopName(member) {
  if (!member) return ''
  if (typeof member === 'object') {
    if (member.shopName) return member.shopName
    const shop = allShops.value.find(s =>
      (member.shopUid && String(s.uid) === String(member.shopUid)) ||
      (member.shopCode && s.code === member.shopCode)
    )
    return shop ? (shop.nameTh || shop.nameEn || member.shopCode || member.shopUid) : (member.shopCode || member.shopUid || '')
  }
  const shop = allShops.value.find(s => s.code === member || String(s.uid) === String(member) || s.nameTh === member)
  return shop ? (shop.nameTh || shop.nameEn || member) : member
}

// Branch Selection in Modal
function isBranchSelected(branch) {
  const branchUid = String(branch.uid ?? '')
  const branchCode = branch.code ?? ''
  return form.members.some(m => {
    if (typeof m === 'object' && m !== null) {
      return (branchUid && String(m.shopUid) === branchUid) ||
             (branchCode && m.shopCode === branchCode)
    }
    return m === branchCode || String(m) === branchUid
  })
}

function toggleBranch(branch) {
  const branchUid = String(branch.uid ?? '')
  const branchCode = branch.code ?? ''
  const existingIdx = form.members.findIndex(m => {
    if (typeof m === 'object' && m !== null) {
      return (branchUid && String(m.shopUid) === branchUid) ||
             (branchCode && m.shopCode === branchCode)
    }
    return m === branchCode || String(m) === branchUid
  })

  if (existingIdx > -1) {
    form.members.splice(existingIdx, 1)
  } else {
    const orig = originalMembers.value.find(m => {
      if (typeof m === 'object' && m !== null) {
        return (branchUid && String(m.shopUid) === branchUid) ||
               (branchCode && m.shopCode === branchCode)
      }
      return m === branchCode || String(m) === branchUid
    })

    const newMember = {
      shopCode: branch.code || '',
      shopName: branch.nameTh || branch.nameEn || branch.code || '',
      shopUid: String(branch.uid ?? '')
    }
    if (orig && typeof orig === 'object' && orig.uid) {
      newMember.uid = String(orig.uid)
    }
    form.members.push(newMember)
  }
}

function toggleSelectAll() {
  const list = filteredModalBranches.value
  if (isAllFilteredSelected.value) {
    // Unselect all filtered
    list.forEach(b => {
      const bUid = String(b.uid ?? '')
      const bCode = b.code ?? ''
      const idx = form.members.findIndex(m => {
        if (typeof m === 'object' && m !== null) {
          return (bUid && String(m.shopUid) === bUid) ||
                 (bCode && m.shopCode === bCode)
        }
        return m === bCode || String(m) === bUid
      })
      if (idx > -1) form.members.splice(idx, 1)
    })
  } else {
    // Select all filtered
    list.forEach(b => {
      if (!isBranchSelected(b)) {
        const bUid = String(b.uid ?? '')
        const bCode = b.code ?? ''
        const orig = originalMembers.value.find(m => {
          if (typeof m === 'object' && m !== null) {
            return (bUid && String(m.shopUid) === bUid) ||
                   (bCode && m.shopCode === bCode)
          }
          return m === bCode || String(m) === bUid
        })
        const newMember = {
          shopCode: b.code || '',
          shopName: b.nameTh || b.nameEn || b.code || '',
          shopUid: String(b.uid ?? '')
        }
        if (orig && typeof orig === 'object' && orig.uid) {
          newMember.uid = String(orig.uid)
        }
        form.members.push(newMember)
      }
    })
  }
}

// Open Modal: Add Mode
function openAddModal() {
  isEditMode.value = false
  form.uid = ''
  form.name = ''
  form.members = []
  form.version = '1'
  originalMembers.value = []
  searchBranchText.value = ''
  showNameError.value = false
  openModalElement('shopGroupModal')
}

// Open Modal: Edit Mode
function openEditModal(group) {
  isEditMode.value = true
  form.uid = group.uid || ''
  form.name = group.name || ''
  form.version = group.version != null ? String(group.version) : '1'
  searchBranchText.value = ''
  showNameError.value = false

  const rawMembers = Array.isArray(group.members) ? group.members : []
  originalMembers.value = JSON.parse(JSON.stringify(rawMembers))

  form.members = rawMembers.map(m => {
    if (typeof m === 'object' && m !== null) {
      const shop = allShops.value.find(s =>
        (m.shopUid && String(s.uid) === String(m.shopUid)) ||
        (m.shopCode && s.code === m.shopCode)
      )
      return {
        uid: m.uid ? String(m.uid) : '',
        shopCode: m.shopCode || shop?.code || '',
        shopName: m.shopName || shop?.nameTh || shop?.nameEn || '',
        shopUid: String(m.shopUid ?? shop?.uid ?? '')
      }
    }
    // Fallback if legacy string
    const shop = allShops.value.find(s => s.code === m || String(s.uid) === String(m))
    return {
      uid: '',
      shopCode: shop?.code || String(m),
      shopName: shop?.nameTh || shop?.nameEn || '',
      shopUid: shop?.uid ? String(shop.uid) : ''
    }
  })

  openModalElement('shopGroupModal')
}

// Close Modal
function closeModal() {
  closeModalElement('shopGroupModal')
}

// Bootstrap Modal Helpers
function openModalElement(modalId) {
  const el = document.getElementById(modalId)
  if (el) {
    const modalInstance = Modal.getInstance(el) || new Modal(el)
    modalInstance.show()
  }
}

function closeModalElement(modalId) {
  const el = document.getElementById(modalId)
  if (el) {
    const modalInstance = Modal.getInstance(el) || new Modal(el)
    modalInstance.hide()
  }
}

// Save (Add / Update)
async function handleSave() {
  if (!form.name.trim()) {
    showNameError.value = true
    return
  }
  showNameError.value = false

  if (form.members.length === 0) {
    swal("แจ้งเตือน", "กรุณาเลือกสาขาอย่างน้อย 1 สาขา", "warning")
    return
  }

  isSubmitting.value = true
  try {
    const formattedMembers = form.members.map(m => {
      const item = {
        shopCode: m.shopCode || '',
        shopName: m.shopName || '',
        shopUid: String(m.shopUid ?? '')
      }

      // 🟢 จะแนบ uid เข้าไปก็ต่อเมื่อ "อยู่ในโหมดแก้ไข" และ "m.uid มีค่า" เท่านั้น
      if (isEditMode.value && m.uid) {
        item.uid = String(m.uid)
      }

      return item
    })

    if (isEditMode.value) {
      const payload = {
        uid: form.uid,
        name: form.name.trim(),
        members: formattedMembers,
        version: String(form.version ?? '1')
      }
      const res = await sgroupStore.shopGroupUpdateFetch(payload)
      // console.log("shopGroupUpdateFetch res:", res)

      if (res && (res.status === 200 || res.data?.rtnDesc === "success")) {
        swal("สำเร็จ", "แก้ไขกลุ่มสาขาเรียบร้อยแล้ว", "success")
        setTimeout(async () => {
        sgroupStore.clearGroupsCache()
        // สั่งส่งสัญญาณบอกแท็บอื่นๆ ผ่าน BroadcastChannel
        const bc = new BroadcastChannel('shop_group_channel')
        bc.postMessage({ event: 'refresh_groups' })
        bc.close()
        closeModal()
        await shopGroupListFetch()
      }, 200)
      } else {
        swal("ผิดพลาด", res?.data?.rtnMsg || "ไม่สามารถแก้ไขกลุ่มสาขาได้", "error")
      }
    } else {
      const payload = {
        name: form.name.trim(),
        members: formattedMembers
      }
      const res = await sgroupStore.shopGroupAddFetch(payload)
      // console.log("shopGroupAddFetch res:", res)

      if (res && (res.status === 200 || res.data?.rtnDesc === "success")) {
        swal("สำเร็จ", "เพิ่มกลุ่มสาขาเรียบร้อยแล้ว", "success")
        setTimeout(async () => {
          sgroupStore.clearGroupsCache()
          const bc = new BroadcastChannel('shop_group_channel')
          bc.postMessage({ event: 'refresh_groups' })
          bc.close()
          closeModal()
          await shopGroupListFetch()
        }, 200)
      } else {
        swal("ผิดพลาด", res?.data?.rtnMsg || "ไม่สามารถเพิ่มกลุ่มสาขาได้", "error")
      }
    }
  } catch (error) {
    console.error("Save error:", error)
    swal("ผิดพลาด", error.response?.data?.rtnMsg || "เกิดข้อผิดพลาดในการบันทึก", "error")
  } finally {
    isSubmitting.value = false
  }
}

// Delete
async function handleDelete(group) {
  swal({
    title: "ยืนยันการลบกลุ่มสาขา",
    text: `คุณต้องการลบกลุ่ม "${group.name}" ใช่ไหม?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "ตกลง",
    cancelButtonText: "ยกเลิก",
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#64748b",
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const payload = { uid: group.uid }
        const res = await sgroupStore.shopGroupDeleteFetch(payload)
        // console.log("shopGroupDeleteFetch res:", res)

        if (res && (res.status === 200 || res.data?.rtnDesc === "success")) {
          swal("สำเร็จ", "ลบกลุ่มสาขาเรียบร้อยแล้ว", "success")
          sgroupStore.clearGroupsCache()
          const bc = new BroadcastChannel('shop_group_channel')
          bc.postMessage({ event: 'refresh_groups' })
          bc.close()
          await shopGroupListFetch()
        } else {
          swal("ผิดพลาด", res?.data?.rtnMsg || "ไม่สามารถลบกลุ่มสาขาได้", "error")
        }
      } catch (error) {
        console.error("Delete error:", error)
        swal("ผิดพลาด", error.response?.data?.rtnMsg || "เกิดข้อผิดพลาดในการลบ", "error")
      }
    }
  })
}
</script>

<style lang="scss" scoped>
// Palette
$green-header: #1b4d36;
$green-header-hover: #143d2b;
$green-primary: #00a65a;
$green-pill-bg: #e6f7ef;
$green-pill-text: #198754;
$gray-subtitle: #8c98a4;
$border-color: #e9ecef;
$danger-color: #ef4444;
$blue-color: #3b82f6;

.main-shop-card {
  border-radius: 12px;
  background-color: #ffffff;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2b3445;
}

.page-subtitle {
  font-size: 0.85rem;
  color: $gray-subtitle;

  .dashboard-link {
    color: #4b5563;
    text-decoration: underline;
    &:hover {
      color: $green-primary;
    }
  }
}

// Add Button
.btn-add-group {
  background-color: $green-primary;
  color: #ffffff;
  font-weight: 500;
  font-size: 0.875rem;
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  transition: all 0.2s ease;

  .btn-icon-plus {
    font-size: 1.1rem;
    line-height: 1;
    font-weight: 600;
  }

  &:hover {
    background-color: darken($green-primary, 8%);
    color: #ffffff;
    box-shadow: 0 4px 10px rgba(0, 166, 90, 0.25);
  }
}

// Table Styles
.custom-table {
  border-collapse: separate;
  border-spacing: 0;

  thead.table-dark-green th {
    background-color: $green-header !important;
    color: #ffffff !important;
    font-weight: 500;
    font-size: 0.875rem;
    padding: 12px 16px;
    border: none;
  }

  .col-index {
    width: 65px;
  }
  .col-name {
    width: 200px;
  }
  .col-members {
    min-width: 300px;
  }
  .col-action {
    width: 130px;
  }

  tbody tr {
    transition: background-color 0.15s ease;
    border-bottom: 1px solid #f1f5f9;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #fafbfc;
    }

    td {
      padding: 14px 16px;
      border-bottom: 1px solid #f1f5f9;
    }
  }
}

.group-name {
  font-weight: 600;
  color: #334155;
  font-size: 0.95rem;
}

.branch-count-text {
  font-size: 0.775rem;
  color: #94a3b8;
  margin-top: 2px;
}

// Branch Pill
.branch-pill {
  display: inline-block;
  background-color: $green-pill-bg;
  color: $green-pill-text;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 3px 12px;
  border-radius: 9999px;
  letter-spacing: 0.2px;
}

// Action Circle Buttons
.btn-action-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  padding: 0;
  transition: all 0.2s ease;
  cursor: pointer;

  .action-svg {
    width: 15px;
    height: 15px;
  }

  &.btn-action-edit {
    border: 1px solid $blue-color;
    color: $blue-color;

    &:hover {
      background-color: rgba(59, 130, 246, 0.1);
      transform: scale(1.05);
    }
  }

  &.btn-action-delete {
    border: 1px solid $danger-color;
    color: $danger-color;

    &:hover {
      background-color: rgba(239, 68, 68, 0.1);
      transform: scale(1.05);
    }
  }
}

// Modal Styles
.custom-modal-content {
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.text-dark-title {
  color: #2b3445;
  font-size: 1.15rem;
}

.btn-close-modal {
  background: transparent;
  border: none;
  color: #94a3b8;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #475569;
  }
}

.text-form-label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.custom-input {
  border-radius: 8px;
  border: 1px solid #d1d5db;
  padding: 8px 12px;
  font-size: 0.9rem;
  color: #334155;

  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
  }
}

.btn-outline-select-all {
  border: 1px solid #10b981;
  color: #10b981;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: #ecfdf5;
    border-color: #059669;
    color: #059669;
  }
}

.branch-counter-text {
  font-size: 0.85rem;
  color: #64748b;
}

.branch-table-container {
  max-height: 260px;
  overflow-y: auto;
}

.branch-table {
  border-collapse: separate;
  border-spacing: 0;

  thead.table-dark-green th {
    background-color: $green-header !important;
    color: #ffffff !important;
    font-weight: 500;
    font-size: 0.85rem;
    padding: 10px 14px;
    border: none;
  }

  tbody tr {
    transition: background-color 0.15s ease;
    border-bottom: 1px solid #f1f5f9;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #f8fafc;
    }

    td {
      padding: 10px 14px;
      font-size: 0.875rem;
      border-bottom: 1px solid #f1f5f9;
    }
  }
}

.custom-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid #cbd5e1;
  cursor: pointer;

  &:checked {
    background-color: #10b981;
    border-color: #10b981;
  }
}

.branch-code-cell {
  font-weight: 500;
  color: #475569;
}

.branch-name-cell {
  color: #334155;
}

.validation-hint {
  font-size: 0.775rem;
  color: #94a3b8;
}

// Modal Footer Buttons
.btn-modal-cancel {
  background-color: #ea3a3a;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: darken(#ea3a3a, 8%);
    color: #ffffff;
  }
}

.btn-modal-save {
  background-color: #5bbd82;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: darken(#5bbd82, 8%);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}
</style>
